import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Modal, message } from 'antd';
import {
  buildTaskPrompt,
  PROMPT_APP_URLS,
  PromptTarget,
  TaskPromptContext,
  taskPromptAppUrl
} from '../../../../../utils/taskPrompt';
import track from '../../../../../../../redesign/javascript/utils/analytics';
import { getLangKey } from '../../../../../models/Language';
import * as translations from './strings';

const strings = translations[getLangKey()];
type Fallback = { prompt: string; target?: PromptTarget; manualCopy?: boolean };

export default function useTaskPrompt(
  context: TaskPromptContext | undefined,
  container: HTMLDivElement
) {
  const taskKey = context
    ? `${context.projectId}/${context.taskNumber}`
    : undefined;
  const activeTaskKey = useRef(taskKey);
  activeTaskKey.current = taskKey;
  const [fallback, setFallback] = useState<Fallback | null>(null);
  useEffect(() => setFallback(null), [context?.projectId, context?.taskNumber]);

  const openLabels: Record<PromptTarget, string> = {
    cursor: strings.openCursor,
    claude: strings.openClaude,
    codex: strings.openCodex,
    vscode: strings.openVsCode
  };

  const copy = async (prompt: string, target?: PromptTarget) => {
    try {
      await navigator.clipboard.writeText(prompt);
      if (activeTaskKey.current === taskKey)
        message.success(strings.promptCopied);
    } catch {
      if (activeTaskKey.current === taskKey)
        setFallback({ prompt, target, manualCopy: true });
    }
  };

  const copyPrompt = () => {
    if (context) {
      track('Task prompt action', { action: 'copy' });
      void copy(buildTaskPrompt(context));
    }
  };

  const copyPromptRef = useRef(copyPrompt);
  copyPromptRef.current = copyPrompt;
  useEffect(() => {
    if (!context) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        event.repeat ||
        event.altKey ||
        !(event.metaKey || event.ctrlKey) ||
        !event.shiftKey ||
        event.key.toLowerCase() !== 'p'
      )
        return;
      event.preventDefault();
      event.stopPropagation();
      copyPromptRef.current();
    };
    const documents = new Set<Document>([
      document,
      container?.ownerDocument || document
    ]);
    try {
      if (window.parent.document) documents.add(window.parent.document);
    } catch {
      // Cross-origin parent documents cannot be accessed by the sidebar.
    }
    documents.forEach(doc => doc.addEventListener('keydown', onKeyDown, true));
    return () =>
      documents.forEach(doc =>
        doc.removeEventListener('keydown', onKeyDown, true)
      );
  }, [!!context, container]);

  const openIn = (target: PromptTarget) => {
    if (!context) return;
    track('Task prompt action', { action: 'open', target });
    const prompt = buildTaskPrompt(context);
    const url = taskPromptAppUrl(target, prompt);
    if (!url) {
      setFallback({ prompt, target });
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const dialog = (
    <Modal
      open={!!fallback}
      title={strings.copyPrompt}
      getContainer={() => container || document.body}
      onCancel={() => setFallback(null)}
      footer={[
        <Button key="close" onClick={() => setFallback(null)}>
          {strings.closePrompt}
        </Button>,
        <Button
          key="copy"
          onClick={() =>
            fallback && void copy(fallback.prompt, fallback.target)
          }
        >
          {strings.copyPrompt}
        </Button>,
        fallback?.target && (
          <Button
            key="open"
            href={PROMPT_APP_URLS[fallback.target]}
            target="_blank"
            rel="noopener noreferrer"
          >
            {openLabels[fallback.target]}
          </Button>
        )
      ]}
    >
      <p>
        {fallback?.manualCopy
          ? strings.promptCopyFailed
          : strings.promptTooLong}
      </p>
      <Input.TextArea
        aria-label={strings.taskPrompt}
        value={fallback?.prompt || ''}
        readOnly
        autoSize={{ minRows: 8, maxRows: 16 }}
        onFocus={event => event.target.select()}
      />
    </Modal>
  );

  return { copyPrompt, openIn, dialog };
}
